import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useAlert } from "../../providers/AlertProvider";
import { reorder_drops } from "../../api/route";
import UnallocatedTable from "./UnallocatedTable";
import AllocatedTable from "./AllocatedTable";
import SubbedTable from "./SubbedTable";
import DriverRunBox from "./DriverRunBox";
import ClassicRouteTable from "./ClassicRouteTable";
import "../css/RouteContainer.css";

const RouteContainer = ({
    routes,
    deleteRoute,
    removeDriver,
    removeVehicle,
    theme,
    onDragStart,
    onDragOver,
    onDrop,
    onAssignDriver,
    isUserOnHoliday,
    showJobComments,
    showJobCostings,
    showInvoiceTo,
    showStartTime,
    isLocked,
    showCostings,
    showAddFromDropdown,
    toggleConfirmed,
    vehicleServiceList,
    vehicleOverdueList,
    vorList,
    contractRoute,
    showDemurrage,
    completedRouteUuids,
    onViewCompletion,
    canEdit,
    editSubcontract,
    showDeliveryDetails,
    deliveryAlerts,
    onPodOff,
    moveRoute,
    onReassignDriver,
    onConfirmAllForDriver,
    onUnconfirmAllForDriver,
    drivers,
    vehicles,
    classicView,
}) => {
    const { showAlert } = useAlert();
    const [localRoutes, setLocalRoutes] = useState([]);
    const showCustomerConsignment = localRoutes.some((route) => route.customer_consignment_number);
    const showNetworkFreightMetrics = localRoutes.some((route) => route.is_network_freight);

    useEffect(() => {
        if (!routes) return;
        setLocalRoutes((prev) => {
            // Merge incoming routes with local state, preserving locally-managed
            // drop_number values which may be ahead of the parent's state.
            const localMap = Object.fromEntries(prev.map((r) => [r.route_uuid, r]));
            return routes.map((r) => {
                const local = localMap[r.route_uuid];
                if (!local) return r;
                return { ...r, drop_number: local.profile_id === r.profile_id ? local.drop_number : r.drop_number };
            });
        });
    }, [routes]);

    // Unallocated: no driver, not subbed, sorted by job name
    const unallocated = useMemo(() =>
        localRoutes
            .filter((r) => !r.profile_id && !r.is_subbed)
            .sort((a, b) => (a.job || "").localeCompare(b.job || "")),
        [localRoutes]
    );

    // Subcontracted jobs — shown at the bottom, sorted by job name
    const subbedRoutes = useMemo(() =>
        localRoutes
            .filter((r) => r.is_subbed)
            .sort((a, b) => (a.job || "").localeCompare(b.job || "")),
        [localRoutes]
    );

    // Group allocated routes by driver, each group sorted by drop_number
    const driverGroups = useMemo(() => {
        const allocated = localRoutes.filter((r) => r.profile_id);
        const groups = {};
        for (const r of allocated) {
            const key = r.driver || r.profile_id;
            if (!groups[key]) {
                groups[key] = { driverName: r.driver || "Unknown", driverId: r.profile_id, routes: [] };
            }
            groups[key].routes.push(r);
        }
        for (const key of Object.keys(groups)) {
            groups[key].routes.sort((a, b) => {
                if (a.drop_number == null && b.drop_number == null) return 0;
                if (a.drop_number == null) return 1;
                if (b.drop_number == null) return -1;
                return a.drop_number - b.drop_number;
            });
        }
        return Object.values(groups).sort((a, b) => a.driverName.localeCompare(b.driverName));
    }, [localRoutes]);

    // Reorder within one driver box
    const handleReorder = useCallback(async (updatedItems) => {
        setLocalRoutes((prev) => {
            const map = Object.fromEntries(updatedItems.map((u) => [u.route_uuid, u.drop_number]));
            return prev.map((r) => map[r.route_uuid] !== undefined ? { ...r, drop_number: map[r.route_uuid] } : r);
        });
        try {
            const { status } = await reorder_drops(updatedItems);
            if (status !== 200 && status !== 207) {
                showAlert({ message: "Failed to save drop order.", type: "danger", buttons: [{ type: "danger", label: "Close" }] });
                setLocalRoutes([...routes]);
            }
        } catch (error) {
            setLocalRoutes([...routes]);
            throw error;
        }
    }, [showAlert, routes]);

    // Reassign: drag a row from one driver box to another
    const handleReassign = useCallback(async (route, newDriverName) => {
        const targetGroup = driverGroups.find((g) => g.driverName === newDriverName);
        if (!targetGroup) return;
        const newDropNumber = targetGroup.routes.length + 1;

        // Resolve the new driver's default truck
        const driverProfile = (drivers ?? []).find((d) => d.id === targetGroup.driverId);
        const newTruckId  = driverProfile?.truck_id ?? null;
        const newTruck    = newTruckId ? (vehicles ?? []).find((v) => v.id === newTruckId) : null;
        const newTruckReg  = newTruck?.reg  ?? null;
        const newTruckType = newTruck?.type ?? null;

        // Optimistic update
        setLocalRoutes((prev) =>
            prev.map((r) => r.route_uuid !== route.route_uuid ? r : {
                ...r,
                profile_id: targetGroup.driverId,
                driver: targetGroup.driverName,
                driver_id: targetGroup.driverId,
                drop_number: newDropNumber,
                truck_id:  newTruckId,
                truck_reg: newTruckReg,
                truck_type: newTruckType,
            })
        );

        // Recalculate old driver's sequence
        const oldGroup = driverGroups.find((g) => g.driverName === route.driver);
        const reorderList = [];
        if (oldGroup) {
            oldGroup.routes
                .filter((r) => r.route_uuid !== route.route_uuid)
                .forEach((r, i) => reorderList.push({ route_uuid: r.route_uuid, drop_number: i + 1 }));
        }
        reorderList.push({
            route_uuid: route.route_uuid,
            drop_number: newDropNumber,
            profile_id: targetGroup.driverId,
            truck_id: newTruckId,
        });

        const { status } = await reorder_drops(reorderList);
        if (status !== 200 && status !== 207) {
            showAlert({ message: "Failed to reassign route.", type: "danger", buttons: [{ type: "danger", label: "Close" }] });
            setLocalRoutes([...routes]);
        } else {
            onReassignDriver?.(route.route_uuid, {
                profile_id: targetGroup.driverId,
                driver: targetGroup.driverName,
                driver_id: targetGroup.driverId,
                drop_number: newDropNumber,
                truck_id:  newTruckId,
                truck_reg: newTruckReg,
                truck_type: newTruckType,
            });
        }
    }, [driverGroups, routes, drivers, vehicles, showAlert, onReassignDriver]);

    const sharedProps = {
        theme, isLocked: isLocked || !canEdit, showCostings, completedRouteUuids, onViewCompletion,
        deliveryAlerts, showJobComments, showJobCostings, showStartTime, showDemurrage,
        showDeliveryDetails, showAddFromDropdown, toggleConfirmed, contractRoute,
        editSubcontract, canEdit, onPodOff, deleteRoute, removeVehicle, removeDriver,
        vehicleServiceList, vehicleOverdueList, vorList, onConfirmAllForDriver, onUnconfirmAllForDriver,
        moveRoute, showCustomerConsignment, showNetworkFreightMetrics,
    };

    if (!routes || routes.length === 0) {
        return <div style={{ padding: 20, opacity: 0.6 }}>No route data available</div>;
    }

    return (
        <div style={{ width: "100%" }}>
            <div style={{
                fontWeight: 700,
                fontSize: 18,
                padding: '6px 12px',
                marginBottom: 10,
                opacity: 0.75,
                color: '#22c55e',
            }}>
                Total jobs today: {localRoutes.length}
            </div>

            {classicView ? (
                /* ── Classic Route View: single flat table (old style) ── */
                <ClassicRouteTable
                    routes={localRoutes}
                    deleteRoute={deleteRoute}
                    removeDriver={removeDriver}
                    removeVehicle={removeVehicle}
                    theme={theme}
                    onDragOver={onDragOver}
                    onDrop={onDrop}
                    showJobComments={showJobComments}
                    showJobCostings={showJobCostings}
                    showInvoiceTo={showInvoiceTo}
                    showStartTime={showStartTime}
                    isLocked={isLocked || !canEdit}
                    showCostings={showCostings}
                    showAddFromDropdown={showAddFromDropdown}
                    toggleConfirmed={toggleConfirmed}
                    vehicleServiceList={vehicleServiceList}
                    vehicleOverdueList={vehicleOverdueList}
                    vorList={vorList}
                    contractRoute={contractRoute}
                    showDemurrage={showDemurrage}
                    completedRouteUuids={completedRouteUuids}
                    onViewCompletion={onViewCompletion}
                    canEdit={canEdit}
                    editSubcontract={editSubcontract}
                    showDeliveryDetails={showDeliveryDetails}
                    deliveryAlerts={deliveryAlerts}
                    onPodOff={onPodOff}
                    moveRoute={moveRoute}
                    showCustomerConsignment={showCustomerConsignment}
                    showNetworkFreightMetrics={showNetworkFreightMetrics}
                />
            ) : (
                /* ── New Allocation View ── */
                <>
                    <UnallocatedTable routes={unallocated} onDrop={onDrop} onDragOver={onDragOver}
                        drivers={drivers} onAssignDriver={onAssignDriver} isUserOnHoliday={isUserOnHoliday}
                        {...sharedProps} />
                    <AllocatedTable
                        driverGroups={driverGroups}
                        onReorder={handleReorder}
                        onReassign={handleReassign}
                        onExternalDrop={onDrop}
                        onRemoveDriver={removeDriver}
                        {...sharedProps}
                    />
                    <SubbedTable
                        routes={subbedRoutes}
                        completedRouteUuids={completedRouteUuids}
                        onViewCompletion={onViewCompletion}
                        theme={theme}
                        isLocked={isLocked}
                        showCostings={showCostings}
                        showDemurrage={showDemurrage}
                        showDeliveryDetails={showDeliveryDetails}
                        showJobComments={showJobComments}
                        showJobCostings={showJobCostings}
                        showStartTime={showStartTime}
                        deliveryAlerts={deliveryAlerts}
                        toggleConfirmed={toggleConfirmed}
                        contractRoute={contractRoute}
                        editSubcontract={editSubcontract}
                        canEdit={canEdit}
                        onPodOff={onPodOff}
                        deleteRoute={deleteRoute}
                        moveRoute={moveRoute}
                        showCustomerConsignment={showCustomerConsignment}
                        showNetworkFreightMetrics={showNetworkFreightMetrics}
                    />
                </>
            )}
        </div>
    );
};

export default RouteContainer;
